import { motion } from 'framer-motion';
import { Link } from 'react-router-dom';
import { Sparkles, ChevronRight } from 'lucide-react';
import StaticPageLayout from '../../components/StaticPageLayout';
import { AI_EMPLOYEES } from '../../data/employees';
import { useThemeStore } from '../../store/theme';

export default function AIEmployees() {
  const { theme } = useThemeStore();
  const isDark = theme === 'dark';

  return (
    <StaticPageLayout
      title=""
      subtitle=""
      showBackButton={false}
    >
      {/* Breadcrumbs */}
      <nav className="mb-8">
        <ol className={`flex items-center gap-2 text-sm ${isDark ? 'text-gray-400' : 'text-slate-500'}`}>
          <li>
            <Link
              to="/docs"
              className={`transition-colors ${isDark ? 'hover:text-purple-400' : 'hover:text-purple-600'}`}
            >
              All Collections
            </Link>
          </li>
          <li>
            <ChevronRight className="w-4 h-4" />
          </li>
          <li className={isDark ? 'text-white' : 'text-slate-900'}>
            AI Employees
          </li>
        </ol>
      </nav>

      {/* Page Header */}
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        className="mb-10"
      >
        <h1 className={`text-4xl font-bold mb-3 ${isDark ? 'text-white' : 'text-slate-900'}`}>
          Meet Your AI Team
        </h1>
        <p className={`text-lg ${isDark ? 'text-gray-400' : 'text-slate-600'}`}>
          15 specialized AI employees ready to work for your business 24/7.
        </p>
      </motion.div>

      {/* All Employees Grid */}
      <section className="mb-16">
        <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
          {AI_EMPLOYEES.map((employee, index) => {
            // Employees with detail pages
            const hasDetailPage = employee.id === 'agent' || employee.id === 'aria' || employee.id === 'nova' || employee.id === 'atlas' || employee.id === 'sage' || employee.id === 'echo' || employee.id === 'pulse' || employee.id === 'scout' || employee.id === 'ink' || employee.id === 'flux' || employee.id === 'spark' || employee.id === 'orbit' || employee.id === 'sync' || employee.id === 'code' || employee.id === 'pixel' || employee.id === 'legal' || employee.id === 'mvp' || employee.id === 'slides';

            const cardContent = (
              <>
                {/* Badges */}
                <div className="absolute top-4 right-4 flex gap-2">
                  {employee.isNew && (
                    <span className={`px-2 py-1 rounded-full text-xs font-medium flex items-center gap-1 ${
                      isDark ? 'bg-green-500/20 text-green-400' : 'bg-green-100 text-green-600'
                    }`}>
                      <Sparkles className="w-3 h-3" />
                      New
                    </span>
                  )}
                </div>

                {/* Avatar */}
                <div
                  className={`w-16 h-16 rounded-2xl bg-gradient-to-br ${employee.gradient} flex items-center justify-center mb-4 group-hover:scale-110 transition-transform`}
                >
                  <span className="text-3xl">{employee.avatar}</span>
                </div>

                {/* Info */}
                <h3 className={`text-xl font-semibold mb-1 ${isDark ? 'text-white' : 'text-slate-900'}`}>{employee.name}</h3>
                <p className={`text-sm ${isDark ? 'text-purple-400' : 'text-purple-600'}`}>{employee.role}</p>

                {/* View Details indicator for clickable cards */}
                {hasDetailPage && (
                  <div className={`mt-3 flex items-center gap-1 text-sm font-medium ${
                    isDark ? 'text-sky-400' : 'text-sky-600'
                  }`}>
                    <span>View Details</span>
                    <ChevronRight className="w-4 h-4 group-hover:translate-x-1 transition-transform" />
                  </div>
                )}
              </>
            );

            const cardClassName = `group relative block p-6 rounded-2xl border transition-all ${
              hasDetailPage ? 'cursor-pointer' : ''
            } ${
              isDark
                ? 'bg-dark-800/50 border-white/5 hover:border-white/20'
                : 'bg-white border-slate-200 hover:border-purple-300 shadow-sm hover:shadow-md'
            }`;

            return (
              <motion.div
                key={employee.id}
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: index * 0.03 }}
              >
                {hasDetailPage ? (
                  <Link to={`/employees/${employee.id}`} className={cardClassName}>
                    {cardContent}
                  </Link>
                ) : (
                  <div className={cardClassName}>
                    {cardContent}
                  </div>
                )}
              </motion.div>
            );
          })}
        </div>
      </section>

      {/* CTA */}
      <section className={`text-center p-8 rounded-2xl border ${
        isDark
          ? 'bg-gradient-to-br from-purple-500/10 to-cyan-500/10 border-white/5'
          : 'bg-gradient-to-br from-purple-50 to-cyan-50 border-purple-100'
      }`}>
        <h2 className={`text-2xl font-bold mb-4 ${isDark ? 'text-white' : 'text-slate-900'}`}>Ready to Build Your AI Team?</h2>
        <p className={`mb-6 ${isDark ? 'text-gray-400' : 'text-slate-600'}`}>
          Get started for free and experience the future of work.
        </p>
        <Link
          to="/pricing"
          className="inline-flex items-center gap-2 px-6 py-3 bg-gradient-to-r from-purple-500 to-cyan-500 rounded-xl font-medium text-white shadow-lg shadow-purple-500/25 hover:shadow-purple-500/40 transition-shadow"
        >
          Get Started
        </Link>
      </section>
    </StaticPageLayout>
  );
}
